import { useState, useEffect, useCallback } from 'react';
import {
  getMySalonEnrollments,
  approveEnrollment,
  rejectEnrollment,
} from '../../services/coursesService';
import type { SalonEnrollment } from '../../types/courses.types';

interface ProcessResult { success: boolean; error?: string }

interface UseSalonApprovalsResult {
  isLoading: boolean;
  error: string | null;
  pending: SalonEnrollment[];
  processingId: number | null;
  approve: (enrollmentId: number) => Promise<ProcessResult>;
  reject: (enrollmentId: number) => Promise<ProcessResult>;
  refresh: () => void;
}

/**
 * Godkendelses-kø bygget fra GET /portal/salon/enrollments filtreret på
 * status === 'pending' (spec §6 — /portal/salon/pending-enrollments findes ikke).
 */
export function useSalonApprovals(
  employeeId: number | null,
  isOwner: boolean,
  isAuthenticated: boolean
): UseSalonApprovalsResult {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<SalonEnrollment[]>([]);
  const [processingId, setProcessingId] = useState<number | null>(null);

  const fetchPending = useCallback(async () => {
    if (!employeeId || !isAuthenticated || !isOwner) return;
    setIsLoading(true);
    setError(null);
    try {
      const { enrollments } = await getMySalonEnrollments();
      setPending(enrollments.filter((e) => e.status === 'pending'));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Der opstod en fejl');
    } finally {
      setIsLoading(false);
    }
  }, [employeeId, isAuthenticated, isOwner]);

  useEffect(() => { fetchPending(); }, [fetchPending]);

  const approve = useCallback(async (enrollmentId: number): Promise<ProcessResult> => {
    if (!employeeId) return { success: false, error: 'Manglende parametre' };
    setProcessingId(enrollmentId);
    try {
      await approveEnrollment(enrollmentId);
      setPending((list) => list.filter((e) => e.id !== enrollmentId));
      return { success: true };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Fejl ved godkendelse' };
    } finally {
      setProcessingId(null);
    }
  }, [employeeId]);

  const reject = useCallback(async (enrollmentId: number): Promise<ProcessResult> => {
    if (!employeeId) return { success: false, error: 'Manglende parametre' };
    setProcessingId(enrollmentId);
    try {
      await rejectEnrollment(enrollmentId);
      setPending((list) => list.filter((e) => e.id !== enrollmentId));
      return { success: true };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Fejl ved afvisning' };
    } finally {
      setProcessingId(null);
    }
  }, [employeeId]);

  return { isLoading, error, pending, processingId, approve, reject, refresh: fetchPending };
}
